import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { client } from '../../api/client';
import { useAppStore } from '../../store/appStore';
import { useToast } from '../common/Toast';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface PageContext {
  page: string;
  filters?: Record<string, unknown>;
  propertyId?: string;
  listingType?: string;
}

// Polyfill for crypto.randomUUID
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

interface AIAssistantProps {
  propertyId?: string;
  propertyTitle?: string;
  propertyPrice?: number;
  propertyLocation?: string;
  contextType?: 'property' | 'search' | 'general';
}

// Predefined queries per context - expanded with more options
const getPresetQueries = (language: 'sr' | 'en', contextType: string, propertyTitle?: string, page?: string) => {
  const queries = {
    sr: {
      property: [
        { label: '📊 Analiziraj ovaj oglas', prompt: `Molim te analiziraj oglas za "${propertyTitle || 'ovu nekretninu'}" - cenu, lokaciju, karakteristike i daj svoju procenu.` },
        { label: 'Analiza cene', prompt: `Da li je cena za "${propertyTitle || 'ovu nekretninu'}" fer za ovaj region?` },
        { label: 'Dokumentacija', prompt: 'Koja dokumentacija mi je potrebna za kupovinu ove nekretnine?' },
        { label: 'Sumnjivi elementi', prompt: 'Da li ima sumnjivih elemenata u ovom oglasu na koje treba obratiti pažnju?' },
        { label: 'Pregovaranje', prompt: 'Daj mi savete za pregovaranje o ceni ove nekretnine.' },
        { label: 'Lokacija', prompt: 'Kakva je ova lokacija - infrastruktura, prevoz, sadržaji u blizini?' },
      ],
      search: [
        { label: 'Tržišne cene', prompt: 'Koje su prosečne cene nekretnina u ovom regionu?' },
        { label: 'Investicioni potencijal', prompt: 'Koji regioni u Srbiji imaju najbolji investicioni potencijal?' },
        { label: 'Saveti za kupce', prompt: 'Na šta obratiti pažnju pri kupovini nekretnine?' },
        { label: 'Kako pretraživati', prompt: 'Kako da efikasno koristim filtere za pretragu?' },
      ],
      general: [
        { label: 'O portalu', prompt: 'Kako koristiti Svoj Na Svom portal? Objasni mi glavne funkcije.' },
        { label: 'Token sistem', prompt: 'Kako funkcioniše token sistem za oglašavanje?' },
        { label: 'Cenovnik', prompt: 'Koje su cene za oglašavanje i pakete tokena? Kako da se najpovoljnije oglasim?' },
        { label: 'Prijavi problem', prompt: 'Želim da prijavim problem ili zloupotrebu na sajtu.' },
      ],
      faq: [
        { label: 'Registracija', prompt: 'Kako se registrujem i šta dobijam registracijom?' },
        { label: 'Oglašavanje', prompt: 'Kako da postavim oglas za moju nekretninu?' },
        { label: 'Aukcije', prompt: 'Kako funkcionišu aukcije nekretnina?' },
        { label: 'Sigurnost', prompt: 'Kako proveriti da li je oglas legitiman?' },
      ],
      'my-listings': [
        { label: 'Izmena oglasa', prompt: 'Kako da izmenim ili produžim moj oglas?' },
        { label: 'Statistika', prompt: 'Kako da vidim statistiku pregleda mojih oglasa?' },
        { label: 'Tokeni', prompt: 'Koliko tokena mi treba i kako da ih kupim?' },
      ],
      auctions: [
        { label: 'Kako licitirati', prompt: 'Kako da učestvujem u aukciji?' },
        { label: 'Pravila aukcije', prompt: 'Koja su pravila aukcije i šta se dešava kad pobedim?' },
        { label: 'Sigurnost licitacije', prompt: 'Da li je licitiranje bezbedno?' },
      ],
    },
    en: {
      property: [
        { label: '📊 Analyze this listing', prompt: `Please analyze the listing for "${propertyTitle || 'this property'}" - price, location, features and give your assessment.` },
        { label: 'Price Analysis', prompt: `Is the price for "${propertyTitle || 'this property'}" fair for this region?` },
        { label: 'Documentation', prompt: 'What documents do I need to buy this property?' },
        { label: 'Suspicious Elements', prompt: 'Are there any suspicious elements in this listing I should be aware of?' },
        { label: 'Negotiation', prompt: 'Give me tips for negotiating the price of this property.' },
        { label: 'Location', prompt: 'How is this location - infrastructure, transport, nearby amenities?' },
      ],
      search: [
        { label: 'Market Prices', prompt: 'What are the average property prices in this region?' },
        { label: 'Investment Potential', prompt: 'Which regions in Serbia have the best investment potential?' },
        { label: 'Buyer Tips', prompt: 'What should I look out for when buying a property?' },
        { label: 'How to Search', prompt: 'How do I effectively use the search filters?' },
      ],
      general: [
        { label: 'About Portal', prompt: 'How to use Svoj Na Svom portal? Explain the main features.' },
        { label: 'Token System', prompt: 'How does the token system for advertising work?' },
        { label: 'Pricing', prompt: 'What are the prices for advertising and token packages? How to advertise most cost-effectively?' },
        { label: 'Report Issue', prompt: 'I want to report a problem or abuse on the site.' },
      ],
      faq: [
        { label: 'Registration', prompt: 'How do I register and what do I get by registering?' },
        { label: 'Advertising', prompt: 'How do I post a listing for my property?' },
        { label: 'Auctions', prompt: 'How do property auctions work?' },
        { label: 'Safety', prompt: 'How to verify if a listing is legitimate?' },
      ],
      'my-listings': [
        { label: 'Edit Listing', prompt: 'How do I edit or extend my listing?' },
        { label: 'Statistics', prompt: 'How can I see statistics for my listings?' },
        { label: 'Tokens', prompt: 'How many tokens do I need and how do I buy them?' },
      ],
      auctions: [
        { label: 'How to Bid', prompt: 'How do I participate in an auction?' },
        { label: 'Auction Rules', prompt: 'What are the auction rules and what happens when I win?' },
        { label: 'Bidding Safety', prompt: 'Is bidding safe?' },
      ],
    },
  };
  
  // Return context-specific or page-specific queries
  if (page && queries[language][page as keyof typeof queries.sr]) {
    return queries[language][page as keyof typeof queries.sr];
  }
  return queries[language][contextType as keyof typeof queries.sr] || queries[language].general;
};

// Abuse detection keywords
const ABUSE_KEYWORDS = [
  'hack', 'crack', 'illegal', 'pirate', 'bypass', 'steal',
  'bomb', 'weapon', 'drug', 'kill', 'murder', 'attack',
  'password', 'credit card', 'social security', 'bank account',
  'spam', 'scam', 'fraud',
];

export function AIAssistant({ propertyId, propertyTitle, propertyPrice, propertyLocation, contextType = 'general' }: AIAssistantProps) {
  const { t, language } = useI18n();
  const location = useLocation();
  const { user, filters, blockUserAI } = useAppStore();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId] = useState(() => generateId());
  const [showReportConfirm, setShowReportConfirm] = useState(false);
  const [abuseWarningShown, setAbuseWarningShown] = useState(false);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [sendingSupport, setSendingSupport] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load chat history from sessionStorage on mount
  useEffect(() => {
    const savedMessages = sessionStorage.getItem('ai_chat_history');
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      } catch (e) {
        console.error('Failed to parse saved chat history');
      }
    }
  }, []);

  // Save chat history to sessionStorage when messages change
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem('ai_chat_history', JSON.stringify(messages));
    }
  }, [messages]);

  // Clear chat history on logout
  useEffect(() => {
    if (!user) {
      // User logged out, clear chat
      sessionStorage.removeItem('ai_chat_history');
      setMessages([]);
    }
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle support request submission
  const handleSupportRequest = async () => {
    if (!supportMessage.trim()) return;
    
    setSendingSupport(true);
    try {
      const pageContext = getPageContext();
      const response = await client.api.fetch('/api/ai/support-request', {
        method: 'POST',
        body: JSON.stringify({
          message: supportMessage,
          userEmail: user?.email || '',
          userName: user?.name || 'Anonimni korisnik',
          userId: user?.id || null,
          chatHistory: messages.map(m => ({ role: m.role, content: m.content })),
          page: pageContext.page,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(language === 'sr' 
          ? 'Zahtev je prosleđen podršci. Odgovorićemo vam uskoro!' 
          : 'Request sent to support. We will respond soon!');
        setShowSupportForm(false);
        setSupportMessage('');
        
        // Add confirmation message to chat
        setMessages(prev => [...prev, {
          id: generateId(),
          role: 'assistant',
          content: language === 'sr'
            ? '✅ Vaš zahtev je prosleđen timu za podršku. Očekujte odgovor putem email-a ili notifikacije.'
            : '✅ Your request has been forwarded to the support team. Expect a response via email or notification.'
        }]);
      } else {
        toast.error(data.error || 'Greška pri slanju');
      }
    } catch (error) {
      console.error('Support request error:', error);
      toast.error(language === 'sr' ? 'Greška pri slanju zahteva' : 'Error sending request');
    } finally {
      setSendingSupport(false);
    }
  };

  // Build page context for AI awareness - expanded with more details
  const getPageContext = (): PageContext => {
    const pathname = location.pathname;
    let page = 'home';
    
    if (pathname.includes('/ponuda')) page = 'offers';
    else if (pathname.includes('/potraznja')) page = 'demands';
    else if (pathname.includes('/aukcije')) page = 'auctions';
    else if (pathname.includes('/mapa')) page = 'map';
    else if (pathname.includes('/moji-oglasi')) page = 'my-listings';
    else if (pathname.includes('/profil')) page = 'profile';
    else if (pathname.includes('/nekretnina/')) page = 'property-detail';
    else if (pathname.includes('/cesta-pitanja') || pathname.includes('/faq')) page = 'faq';
    else if (pathname.includes('/o-nama') || pathname.includes('/about')) page = 'about';
    else if (pathname.includes('/kontakt') || pathname.includes('/contact')) page = 'contact';
    else if (pathname.includes('/kupi-tokene') || pathname.includes('/buy-tokens')) page = 'buy-tokens';
    else if (pathname.includes('/novi-oglas') || pathname.includes('/new-listing')) page = 'new-listing';
    else if (pathname.includes('/blog')) page = 'blog';
    else if (pathname.includes('/notifikacije')) page = 'notifications';
    
    return {
      page,
      filters: filters as Record<string, unknown>,
      propertyId,
      listingType: contextType,
    };
  };

  // Check for abuse in message - expanded detection
  const checkForAbuse = async (message: string): Promise<boolean> => {
    const lowerMessage = message.toLowerCase();
    const hasAbuseKeyword = ABUSE_KEYWORDS.some(keyword => lowerMessage.includes(keyword));
    
    // Also check for prompt injection attempts
    const injectionPatterns = [
      /ignore.*instructions/i,
      /pretend.*you.*are/i,
      /act.*as.*if/i,
      /system.*prompt/i,
      /bypass/i,
      /jailbreak/i,
    ];
    const hasInjectionAttempt = injectionPatterns.some(pattern => pattern.test(message));
    
    if ((hasAbuseKeyword || hasInjectionAttempt) && !abuseWarningShown) {
      // Show warning to user
      setAbuseWarningShown(true);
      
      // Block user if logged in
      if (user) {
        blockUserAI(user.id);
      }
      
      // Report to admin in background
      try {
        const pageContext = getPageContext();
        await client.api.fetch('/api/ai/report', {
          method: 'POST',
          body: JSON.stringify({
            conversationId,
            reason: hasInjectionAttempt ? 'prompt_injection' : 'auto_detected',
            messages: [...messages, { role: 'user', content: message }].map((m) => ({
              role: m.role,
              content: m.content,
            })),
            currentPage: pageContext.page,
            detectedKeywords: hasAbuseKeyword ? ABUSE_KEYWORDS.filter(k => lowerMessage.includes(k)) : ['injection_attempt'],
          }),
        });
      } catch (error) {
        console.error('Auto-report error:', error);
      }
      
      return true;
    }
    return false;
  };

  const handleSubmit = async (e: React.FormEvent, presetPrompt?: string, isPresetQuery?: boolean) => {
    e?.preventDefault();
    const messageContent = presetPrompt || input.trim();
    if (!messageContent || isLoading) return;

    // Check for abuse
    const isAbusive = await checkForAbuse(messageContent);
    if (isAbusive) {
      const warningMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: language === 'sr' 
          ? 'Vaše pitanje sadrži neprikladan sadržaj. Molimo vas da postavljate pitanja vezana za nekretnine. Administrator je obavešten.'
          : 'Your question contains inappropriate content. Please ask questions related to real estate. The administrator has been notified.',
      };
      setMessages((prev) => [...prev, { id: generateId(), role: 'user', content: messageContent }, warningMessage]);
      setInput('');
      return;
    }

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: messageContent,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Create placeholder for assistant message
    const assistantId = generateId();
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: 'assistant', content: '' },
    ]);

    try {
      const pageContext = getPageContext();
      
      // Get last 5 messages for context (excluding the current one)
      const recentMessages = [...messages, userMessage].slice(-6).map((m) => ({
        role: m.role,
        content: m.content,
      }));
      
      const response = await client.api.fetch('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          messages: recentMessages,
          language,
          isPreset: isPresetQuery || false, // Flag for preset questions
          context: {
            ...pageContext,
            propertyTitle,
            propertyPrice,
            propertyLocation,
            currentUrl: window.location.pathname,
            userLoggedIn: !!user,
            userName: user?.name || null,
            userEmail: user?.email || null,
            userId: user?.id || null,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      // Parse JSON response (PHP backend returns JSON, not stream)
      const data = await response.json();
      
      if (data.success && data.response) {
        // Update the assistant message with full content
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: data.response } : m
          )
        );
      } else {
        throw new Error(data.error || 'AI greška');
      }
    } catch (error) {
      console.error('AI chat error:', error);
      
      // Show unavailable message and direct to support
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { 
                  ...m, 
                  content: language === 'sr' 
                    ? 'AI asistent trenutno nije dostupan. 😔\n\nZa pomoć kontaktirajte podršku:\n📧 kancelarija@svojnasvome.com\n📞 +381 65 290 7002 (WhatsApp, Viber, SMS)\n\nIli kliknite na dugme za podršku ispod.'
                    : 'AI assistant is currently unavailable. 😔\n\nFor help contact support:\n📧 kancelarija@svojnasvome.com\n📞 +381 65 290 7002 (WhatsApp, Viber, SMS)\n\nOr click the support button below.'
                }
              : m
          )
        );
      }, 500);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportAbuse = async () => {
    try {
      const pageContext = getPageContext();
      const { user, reportAbuse } = useAppStore.getState();
      
      if (user) {
        reportAbuse(
          user.id,
          'user_report',
          JSON.stringify({
            conversationId,
            messages: messages.map((m) => ({ role: m.role, content: m.content })),
            currentPage: pageContext.page,
          })
        );
      }

      // Try API call as well, but don't block on it
      client.api.fetch('/api/ai/report', {
        method: 'POST',
        body: JSON.stringify({
          conversationId,
          reason: 'user_report',
          messages: messages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          currentPage: pageContext.page,
        }),
      }).catch(console.error);

      setShowReportConfirm(false);
      toast.success(
        language === 'sr' ? 'Prijava poslata' : 'Report sent',
        language === 'sr' ? 'Hvala na prijavi. Administrator će pregledati razgovor.' : 'Thank you for reporting. An administrator will review the conversation.'
      );
    } catch (error) {
      console.error('Report error:', error);
    }
  };

  const presetQueries = getPresetQueries(language, contextType, propertyTitle, getPageContext().page);

  return (
    <>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full shadow-lg flex items-center justify-center text-white hover:shadow-xl transition-shadow"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 right-2 left-2 sm:left-auto sm:right-6 z-50 sm:w-96 h-[70vh] sm:h-[600px] sm:max-h-[80vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700"
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold">{language === 'sr' ? 'AI Asistent' : 'AI Assistant'}</h3>
                  <p className="text-xs opacity-90">{language === 'sr' ? 'Online' : 'Online'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowReportConfirm(true)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                  title={language === 'sr' ? 'Prijavi problem' : 'Report issue'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 dark:bg-slate-900">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600 dark:text-amber-400">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-slate-900 dark:text-white mb-2">
                    {language === 'sr' ? 'Kako vam mogu pomoći?' : 'How can I help you?'}
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                    {language === 'sr' 
                      ? 'Pitajte me o cenama, lokacijama, dokumentaciji ili savetima za kupovinu.'
                      : 'Ask me about prices, locations, documentation, or buying tips.'}
                  </p>
                  
                  <div className="grid grid-cols-1 gap-2">
                    {presetQueries.map((query, index) => (
                      <button
                        key={index}
                        onClick={(e) => handleSubmit(e, query.prompt, true)}
                        className="text-left p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-amber-500 dark:hover:border-amber-500 transition-colors text-sm text-slate-700 dark:text-slate-300 shadow-sm"
                      >
                        {query.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-amber-500 text-white rounded-tr-none'
                        : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-none shadow-sm border border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl rounded-tl-none shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowSupportForm(true)}
                  className="p-2 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  title={language === 'sr' ? 'Kontaktirajte podršku' : 'Contact support'}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </button>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={language === 'sr' ? 'Unesite pitanje...' : 'Type your question...'}
                  className="flex-1 px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="p-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </form>

            {/* Support Form Modal */}
            {showSupportForm && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-xl max-w-sm w-full">
                  <h4 className="font-bold text-lg mb-2 text-slate-900 dark:text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    {language === 'sr' ? 'Kontakt podrška' : 'Contact Support'}
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    {language === 'sr' 
                      ? 'Opišite vaš problem ili pitanje. Administrator će vam odgovoriti putem email-a.'
                      : 'Describe your issue or question. An administrator will respond via email.'}
                  </p>
                  <textarea
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    placeholder={language === 'sr' ? 'Opišite problem ili pitanje...' : 'Describe your issue or question...'}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white text-sm resize-none h-24 mb-4 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setShowSupportForm(false);
                        setSupportMessage('');
                      }}
                      className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-sm"
                    >
                      {language === 'sr' ? 'Otkaži' : 'Cancel'}
                    </button>
                    <button
                      onClick={handleSupportRequest}
                      disabled={!supportMessage.trim() || sendingSupport}
                      className="px-4 py-2 bg-amber-500 text-white hover:bg-amber-600 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {sendingSupport ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {language === 'sr' ? 'Slanje...' : 'Sending...'}
                        </>
                      ) : (
                        language === 'sr' ? 'Pošalji' : 'Send'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Report Confirmation Modal */}
            {showReportConfirm && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-xl max-w-xs w-full">
                  <h4 className="font-bold text-lg mb-2 text-slate-900 dark:text-white">
                    {language === 'sr' ? 'Prijavi razgovor?' : 'Report conversation?'}
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    {language === 'sr' 
                      ? 'Da li ste sigurni da želite da prijavite ovaj razgovor administratorima?'
                      : 'Are you sure you want to report this conversation to administrators?'}
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowReportConfirm(false)}
                      className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-sm"
                    >
                      {language === 'sr' ? 'Otkaži' : 'Cancel'}
                    </button>
                    <button
                      onClick={handleReportAbuse}
                      className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm"
                    >
                      {language === 'sr' ? 'Prijavi' : 'Report'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}